/**
 * An app runs an installed version (RFC 0313 3m).
 *
 * Editing an app, by hand or by an agent, changes its documents and not what
 * anyone runs. Install saves the app as it is now as a version and makes that
 * version live; roll back makes an earlier one live. What an app's users run
 * changes only then.
 *
 * AN APP NEVER STOPS DRAWING ITS RECORDS. Both refuse a version whose schema
 * lacks a field an existing record uses, the rule `updateApp` already keeps
 * for `dataType`, and a refusal names the newest version that draws them all.
 */

import type { AnyDocumentId, Repo } from '@automerge/automerge-repo/slim'
import { Automerge as A } from '@automerge/automerge-repo/slim'
import { projectDocuments } from '../documents/reads'
import { listVersions, saveVersion, type Version } from './versions'

export type InstallResult =
  | { ok: true; version: Version }
  | {
      ok: false
      reason: 'not-found' | 'breaks-records'
      /** Records the version could not draw. */
      records?: number
      /** The newest version that draws every record, when there is one. */
      suggest?: Version
    }

async function ready<T>(repo: Repo, id: string) {
  const h = await repo.find<T>(id as AnyDocumentId)
  await (h as { whenReady?: () => Promise<void> }).whenReady?.()
  return h
}

/** An app's versions, newest first. */
export async function appVersions(
  repo: Repo,
  projectId: string,
  appId: string,
): Promise<Version[]> {
  return (await listVersions(repo, projectId)).filter(
    (v) => v.covers.kind === 'app' && v.covers.appId === appId,
  )
}

/** The field names a version's schema declares. */
async function schemaFieldsAt(
  repo: Repo,
  appId: string,
  version: Version,
): Promise<Set<string> | null> {
  const heads = version.heads[appId]
  if (!heads) return null
  const app = await ready(repo, appId)
  const then = A.view(app.doc() as A.Doc<unknown>, heads) as {
    schema?: { fields?: Record<string, unknown> }
  }
  return new Set(Object.keys(then.schema?.fields ?? {}))
}

/** How many of the app's records use a field the version does not declare. */
async function recordsItBreaks(
  repo: Repo,
  projectId: string,
  appId: string,
  version: Version,
): Promise<number> {
  const fields = await schemaFieldsAt(repo, appId, version)
  // No schema at all means the app never declared one: nothing to check.
  if (!fields || fields.size === 0) return 0
  const app = await ready<{ dataType?: string }>(repo, appId)
  const dataType = app.doc()?.dataType
  if (!dataType) return 0
  const project = await ready(repo, projectId)
  let broken = 0
  for (const ref of projectDocuments(project.doc())) {
    if (ref.dataType !== dataType) continue
    const rec = await ready<{ data?: Record<string, unknown> }>(repo, ref.id)
    const keys = Object.keys(rec.doc()?.data ?? {})
    if (keys.some((k) => !fields.has(k))) broken++
  }
  return broken
}

async function makeLive(
  repo: Repo,
  projectId: string,
  appId: string,
  version: Version,
): Promise<InstallResult> {
  const broken = await recordsItBreaks(repo, projectId, appId, version)
  if (broken > 0) {
    let suggest: Version | undefined
    for (const v of await appVersions(repo, projectId, appId)) {
      if (v.id === version.id) continue
      if ((await recordsItBreaks(repo, projectId, appId, v)) === 0) {
        suggest = v
        break
      }
    }
    return {
      ok: false,
      reason: 'breaks-records',
      records: broken,
      ...(suggest ? { suggest } : {}),
    }
  }
  const app = await ready<{ live?: string }>(repo, appId)
  app.change((d) => {
    d.live = version.id
  })
  return { ok: true, version }
}

/** Save the app as it is now as the next version, and make it live. */
export async function installApp(
  repo: Repo,
  projectId: string,
  appId: string,
  by: string,
): Promise<InstallResult> {
  const existing = await appVersions(repo, projectId, appId)
  const version = await saveVersion(repo, projectId, {
    name: `v${existing.length + 1}`,
    by,
    covers: { kind: 'app', appId },
  })
  return makeLive(repo, projectId, appId, version)
}

/** Make an earlier version live. */
export async function rollbackApp(
  repo: Repo,
  projectId: string,
  appId: string,
  versionId: string,
): Promise<InstallResult> {
  const version = (await appVersions(repo, projectId, appId)).find(
    (v) => v.id === versionId,
  )
  if (!version) return { ok: false, reason: 'not-found' }
  return makeLive(repo, projectId, appId, version)
}

/**
 * Has the app changed since its live version? The documents it covers, at
 * their heads now, against the heads the version saved. Pure over the doc map.
 */
export function changedSinceLive(
  version: Version,
  currentHeads: Record<string, string[]>,
): number {
  let changed = 0
  for (const [id, heads] of Object.entries(currentHeads)) {
    const saved = version.heads[id]
    if (!saved || [...saved].sort().join() !== [...heads].sort().join()) {
      changed++
    }
  }
  return changed
}

/**
 * The view document an app's users run: the one the app record named when its
 * live version was saved, not the one it names now. Repointing an app at a new
 * view is an edit like any other, and does not ship until installed (0313
 * adversarial review, finding 6). Null when the version does not hold the app
 * record. Pure.
 */
export function installedViewDocId(
  appDoc: unknown,
  appId: string,
  version: Version | undefined,
): string | null {
  const heads = version?.heads[appId]
  if (!appDoc || !heads) return null
  try {
    const then = A.view(appDoc as A.Doc<unknown>, heads) as {
      viewDocId?: unknown
    }
    return then.viewDocId == null ? null : String(then.viewDocId)
  } catch {
    return null
  }
}

/**
 * A document as an app's users see it: at the live version's heads when the
 * app has one. A version that does not hold the document answers undefined, so
 * the caller waits rather than running the draft. Pure.
 */
export function atLiveVersion<T>(
  doc: T | undefined,
  documentId: string,
  version: Version | undefined,
): T | undefined {
  if (!doc || !version) return doc
  const heads = version.heads[documentId]
  // Not the draft: finding 6 was a draft run because the version lacked it.
  if (!heads) return undefined
  try {
    return A.view(doc as A.Doc<unknown>, heads) as T
  } catch {
    return doc
  }
}
